import React, { useState, useEffect } from 'react';
import { X, Package, Eye, ShoppingCart, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { DeliveryPersonItem, DeliveryPersonDisplayInfo } from '../types';
import { DeliveryPersonService } from '../services/deliveryPersonService';
import LoadingSpinner from './LoadingSpinner';

interface DeliveryPersonItemsModalProps {
  isOpen: boolean;
  onClose: () => void;
  deliveryPerson: DeliveryPersonDisplayInfo;
  deliveryHash: string;
}

const DeliveryPersonItemsModal: React.FC<DeliveryPersonItemsModalProps> = ({
  isOpen,
  onClose,
  deliveryPerson,
  deliveryHash
}) => {
  const [items, setItems] = useState<DeliveryPersonItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [summaryCollapsed, setSummaryCollapsed] = useState(true); // Start collapsed to maximize item list space

  // Load delivery person items when modal opens
  useEffect(() => {
    if (isOpen && deliveryHash) {
      loadDeliveryPersonItems();
    }
  }, [isOpen, deliveryHash]);

  const loadDeliveryPersonItems = async (forceRefresh = false) => {
    setLoading(true);
    setError(null);

    try {
      console.log('Loading items for delivery person:', deliveryPerson.displayName, forceRefresh ? '(force refresh)' : '(cached)');
      
      // Live update callback - called when each individual item is enhanced
      const handleLiveItemUpdate = (updatedItem: any) => {
        console.log(`🔥 Live update: ${updatedItem.sku} - Name: "${updatedItem.name}" Stock: ${updatedItem.current_stock}`);
        setItems(prevItems => {
          // Create completely new array with updated item
          const itemIndex = prevItems.findIndex(item => item.sku === updatedItem.sku);
          if (itemIndex === -1) return prevItems;
          
          const newItems = [...prevItems];
          newItems[itemIndex] = { ...updatedItem }; // Complete copy of updated item
          
          console.log(`✅ UI Updated: ${updatedItem.sku} at index ${itemIndex}`);
          return newItems;
        });
      };
      
      // Use refresh function if force refresh is requested
      const result = forceRefresh 
        ? await DeliveryPersonService.refreshDeliveryPersonItems(deliveryHash, handleLiveItemUpdate)
        : await DeliveryPersonService.getDeliveryPersonItems(deliveryHash, handleLiveItemUpdate);
      
      if (result.error) {
        setError(result.error);
        setItems([]);
      } else {
        setItems(result.data || []);
        console.log('Loaded delivery person items:', result.data?.length);
        
        // Listen for final completion (all items processed)
        if (result.enhancementPromise) {
          result.enhancementPromise.then(() => {
            console.log('🎉 All live updates complete');
          });
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load delivery person items');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleItemExpansion = (sku: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sku)) {
        newSet.delete(sku);
      } else {
        newSet.add(sku);
      }
      return newSet;
    });
  };

  const getTotalQuantity = () => {
    return items.reduce((total, item) => total + item.totalQuantity, 0);
  };

  const getTotalAmount = () => {
    return items.reduce((total, item) => total + item.totalAmount, 0);
  };

  const getTotalOrders = () => {
    const orderNumbers = new Set<string>();
    items.forEach(item => {
      item.items.forEach(order => {
        orderNumbers.add(order.orderNumber);
      });
    });
    return orderNumbers.size;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm sm:max-w-2xl md:max-w-4xl lg:max-w-5xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-4 sm:p-6 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center space-x-3 sm:space-x-4 min-w-0 flex-1">
            {deliveryPerson.avatar && (
              <img
                src={deliveryPerson.avatar}
                alt={deliveryPerson.name}
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover border-2 border-blue-200 flex-shrink-0"
              />
            )}
            <div className="min-w-0 flex-1">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
                📦 Delivery Items
              </h2>
              <p className="text-sm text-gray-600 truncate">
                {deliveryPerson.displayName}
                {deliveryPerson.phone && (
                  <span className="ml-2 hidden sm:inline">📞 {deliveryPerson.phone}</span>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg flex-shrink-0 ml-2"
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>

        {/* Summary Cards - Collapsible */}
        <div className="border-b">
          <div className="flex items-center justify-between p-3 sm:p-4 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors" onClick={() => setSummaryCollapsed(!summaryCollapsed)}>
            <div className="flex items-center gap-3">
              <h3 className="font-medium text-gray-900">Summary</h3>
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <span className="flex items-center gap-1">
                  <Package className="w-4 h-4 text-blue-600" />
                  {items.length} items
                </span>
                <span className="flex items-center gap-1">
                  <ShoppingCart className="w-4 h-4 text-green-600" />
                  {getTotalQuantity()} qty
                </span>
                <span className="hidden sm:flex items-center gap-1">
                  <span className="text-orange-600">💰</span>
                  MVR {getTotalAmount().toFixed(2)}
                </span>
              </div>
            </div>
            <button className="p-1 hover:bg-gray-200 rounded transition-colors">
              {summaryCollapsed ? (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronUp className="w-5 h-5 text-gray-500" />
              )}
            </button>
          </div>
          
          {!summaryCollapsed && (
            <div className="p-3 sm:p-4 bg-gray-50">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-white p-3 rounded-lg border shadow-sm">
                  <div className="flex items-center space-x-2">
                    <Package className="w-4 h-4 text-blue-600" />
                    <div>
                      <p className="text-xs text-gray-600">Items</p>
                      <p className="text-lg font-semibold text-gray-900">{items.length}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white p-3 rounded-lg border shadow-sm">
                  <div className="flex items-center space-x-2">
                    <ShoppingCart className="w-4 h-4 text-green-600" />
                    <div>
                      <p className="text-xs text-gray-600">Quantity</p>
                      <p className="text-lg font-semibold text-gray-900">{getTotalQuantity()}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-3 rounded-lg border shadow-sm">
                  <div className="flex items-center space-x-2">
                    <Eye className="w-4 h-4 text-purple-600" />
                    <div>
                      <p className="text-xs text-gray-600">Orders</p>
                      <p className="text-lg font-semibold text-gray-900">{getTotalOrders()}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-3 rounded-lg border shadow-sm">
                  <div className="flex items-center space-x-2">
                    <span className="text-orange-600 text-sm">💰</span>
                    <div>
                      <p className="text-xs text-gray-600">Value</p>
                      <p className="text-lg font-semibold text-gray-900">MVR {getTotalAmount().toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4 lg:p-6">
          {loading ? (
                         <div className="flex flex-col items-center justify-center h-64 space-y-4">
               <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
               <p className="text-gray-600">Loading delivery person items...</p>
               <p className="text-sm text-gray-500">
                 Fetching orders and grouping items with images...
               </p>
             </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-64 space-y-4">
              <div className="text-red-500 text-6xl">⚠️</div>
              <div className="text-center">
                <p className="text-lg font-medium text-red-600 mb-2">Error Loading Items</p>
                <p className="text-gray-600 mb-4">{error}</p>
                <button
                  onClick={() => loadDeliveryPersonItems()}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 space-y-4">
              <div className="text-gray-400 text-6xl">📦</div>
              <div className="text-center">
                <p className="text-lg font-medium text-gray-600 mb-2">No Items Found</p>
                <p className="text-gray-500">
                  This delivery person has no pending orders or items.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              {items.map((item, index) => (
                <div
                  key={`${item.sku}-${item.current_stock || 0}-${item.name || 'loading'}`}
                  className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                >
                  {/* Item Header */}
                  <div
                    className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => toggleItemExpansion(item.sku)}
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex-1">
                                                 <div className="flex items-center space-x-3">
                           {/* Item Image */}
                           <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                             {item.image_url || item.primary_image ? (
                               <img
                                 src={item.image_url || item.primary_image}
                                 alt={item.name || item.sku}
                                 className="w-full h-full object-cover"
                                 onError={(e) => {
                                   // Fallback to placeholder if image fails to load
                                   (e.target as HTMLImageElement).style.display = 'none';
                                   (e.target as HTMLImageElement).nextElementSibling!.classList.remove('hidden');
                                 }}
                               />
                             ) : null}
                             <div className={`w-full h-full flex items-center justify-center ${item.image_url || item.primary_image ? 'hidden' : ''}`}>
                               <Package className="w-6 h-6 text-gray-400" />
                             </div>
                           </div>
                           
                           <div className="flex-1">
                             <div className="flex items-center space-x-2">
                               <h3 className="font-medium text-gray-900">
                                 {item.name || item.sku}
                               </h3>
                               {/* Stock level indicator */}
                               {item.current_stock !== undefined && (
                                 <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                   item.current_stock <= 0 ? 'bg-red-100 text-red-800' :
                                   item.current_stock <= 5 ? 'bg-yellow-100 text-yellow-800' :
                                   'bg-green-100 text-green-800'
                                 }`}>
                                   📦 Stock: {item.current_stock}
                                 </span>
                               )}
                             </div>
                             <p className="text-sm text-gray-500">SKU: {item.sku}</p>
                             {item.brand && (
                               <p className="text-xs text-gray-400">Brand: {item.brand}</p>
                             )}
                             {item.current_stock !== undefined && (
                               <p className="text-xs text-blue-600">Stock: {item.current_stock}</p>
                             )}
                           </div>
                         </div>
                      </div>
                      
                      <div className="flex items-center space-x-6">
                        <div className="text-right">
                          <p className="text-sm text-gray-600">Quantity</p>
                          <p className="font-semibold text-gray-900">{item.totalQuantity}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600">Rate</p>
                          <p className="font-semibold text-gray-900">MVR {item.rate.toFixed(2)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600">Total</p>
                          <p className="font-semibold text-green-600">MVR {item.totalAmount.toFixed(2)}</p>
                        </div>
                        <div className="text-gray-400">
                          {expandedItems.has(item.sku) ? '▼' : '▶'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Order Details */}
                  {expandedItems.has(item.sku) && (
                    <div className="border-t border-gray-200 bg-gray-50 p-4">
                      <h4 className="font-medium text-gray-900 mb-3">
                        Orders ({item.items.length}):
                      </h4>
                      <div className="space-y-2">
                        {item.items.map((order, orderIndex) => (
                          <div
                            key={`${order.orderNumber}-${orderIndex}`}
                            className="flex justify-between items-center p-3 bg-white rounded border"
                          >
                            <div>
                              <p className="font-medium text-gray-900">{order.orderNumber}</p>
                              <p className="text-sm text-gray-600">
                                Status: <span className={`
                                  px-2 py-1 rounded-full text-xs font-medium
                                  ${order.orderStatus.toLowerCase() === 'ready' 
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-gray-100 text-gray-800'
                                  }
                                `}>
                                  {order.orderStatus}
                                </span>
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-medium">Qty: {order.quantity}</p>
                              <p className="text-sm text-gray-600">
                                MVR {(order.quantity * (order.rate || item.rate)).toFixed(2)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-4 bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Last updated: {new Date().toLocaleTimeString()}
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => loadDeliveryPersonItems(true)}
                disabled={loading}
                className="px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50"
                title="Force refresh from server (bypasses cache)"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : '🔄'} Refresh
              </button>
              <button
                onClick={onClose}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliveryPersonItemsModal;
